import { useEffect, useState } from 'react';
import Modal from '../Modal';
import { rentalsApi } from '../../lib/api';
import { groupRatesByCarrier, type RateOption } from '../../utils/shippingRates';

interface RatesData {
  available: boolean;
  reason?: string;
  distanceMiles?: number | null;
  fromAddress?: string;
  toAddress?: string;
  item?: { weightLbs?: number | null; lengthIn?: number | null; widthIn?: number | null; heightIn?: number | null };
  rates: RateOption[];
}

interface SetupShippingModalProps {
  isOpen: boolean;
  rentalId: string;
  onClose: () => void;
  onPurchased: () => void;
}

// Buys a real Shippo label for a Shipping rental: a fresh live rate list
// (re-quoted at ship-time, since the approval-time estimate may be stale or on
// a carrier the owner no longer wants), a carrier picked from that list with
// its own real, already-quoted price (no manual override needed), and "Pay
// Shipping" purchases it -- returns a guaranteed price, a real tracking
// number, and a label PDF link, all together, immediately.
export default function SetupShippingModal({ isOpen, rentalId, onClose, onPurchased }: SetupShippingModalProps) {
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RatesData | null>(null);
  const [selectedRateId, setSelectedRateId] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    setData(null);
    rentalsApi.getShippingRates(rentalId)
      .then((res) => {
        if (res.data.success) {
          setData(res.data.data);
          if (res.data.data.rates?.length) {
            setSelectedRateId(res.data.data.rates[0].rateId);
            setSelectedCarrier(res.data.data.rates[0].carrier || '');
          }
        }
      })
      .catch((err: any) => setError(err?.response?.data?.error || 'Failed to load shipping rates'))
      .finally(() => setLoading(false));
  }, [isOpen, rentalId]);

  const handlePurchase = async () => {
    if (!selectedRateId) return;
    setPurchasing(true);
    setError(null);
    try {
      await rentalsApi.purchaseShippingLabel(rentalId, { rateId: selectedRateId });
      onPurchased();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to purchase shipping label');
    } finally {
      setPurchasing(false);
    }
  };

  const carrierGroups = data ? groupRatesByCarrier(data.rates) : [];
  const currentGroup = carrierGroups.find((g) => g.carrier === selectedCarrier) || carrierGroups[0];
  const selectedRate = currentGroup?.rates.find((r) => r.rateId === selectedRateId) || currentGroup?.rates[0];
  const item = data?.item;

  return (
    <Modal
      isOpen={isOpen}
      title="Setup Shipping"
      confirmLabel={purchasing ? 'Purchasing...' : selectedRate ? `Pay Shipping — $${selectedRate.amount}` : 'Pay Shipping'}
      cancelLabel="Cancel"
      onConfirm={data?.available && selectedRateId ? handlePurchase : undefined}
      onCancel={() => {
        if (!purchasing) onClose();
      }}
      confirmVariant="accent"
      disabled={purchasing || loading}
      confirmDisabled={!data?.available}
    >
      <div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mb-3">{error}</div>
        )}

        {loading ? (
          <p className="text-sm text-[var(--text-secondary)]">Getting live shipping rates...</p>
        ) : !data?.available ? (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-1">
            <p className="font-medium">No shipping rate is available right now.</p>
            {data?.reason && <p>{data.reason}</p>}
          </div>
        ) : (
          <div className="space-y-4">
            {(data.fromAddress || data.toAddress || data.distanceMiles != null) && (
              <div className="text-xs text-[var(--text-secondary)] space-y-0.5 bg-[var(--bg-primary)] rounded-lg p-3">
                {data.fromAddress && <div>Ship from: {data.fromAddress}</div>}
                {data.toAddress && <div>Ship to: {data.toAddress}</div>}
                {data.distanceMiles != null && <div>Distance: {Math.round(data.distanceMiles).toLocaleString()} mi</div>}
                {(item?.weightLbs != null || item?.lengthIn != null) && (
                  <div>
                    Item: {item?.weightLbs != null ? `${item.weightLbs} lbs` : 'weight not set'}
                    {item?.lengthIn != null && item?.widthIn != null && item?.heightIn != null
                      ? ` — ${item.lengthIn}"×${item.widthIn}"×${item.heightIn}"`
                      : ''}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Carrier</label>
                <select
                  value={currentGroup?.carrier || ''}
                  onChange={(e) => {
                    const group = carrierGroups.find((g) => g.carrier === e.target.value);
                    setSelectedCarrier(e.target.value);
                    setSelectedRateId(group?.rates[0]?.rateId || '');
                  }}
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                >
                  {carrierGroups.map((g) => (
                    <option key={g.carrier} value={g.carrier}>{g.carrier}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Shipping method</label>
                <select
                  value={selectedRateId}
                  onChange={(e) => setSelectedRateId(e.target.value)}
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--forest-green)] focus:border-[var(--forest-green)]"
                >
                  {currentGroup?.rates.map((r) => (
                    <option key={r.rateId} value={r.rateId}>
                      {r.serviceLevel || 'Standard'} — ${r.amount}
                      {r.estimatedDays != null ? ` (${r.estimatedDays}d)` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedRate && (
              <div className="flex justify-between text-sm border-t border-[var(--border)] pt-3">
                <span className="text-[var(--text-secondary)]">Cost</span>
                <span className="font-semibold text-[var(--text-primary)]">${selectedRate.amount}</span>
              </div>
            )}

            <p className="text-xs text-[var(--text-muted)]">
              A label is purchased at a locked price with a real tracking number, generated immediately —
              print it and drop off the package, or schedule a carrier pickup.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
